import React, { useState, useEffect, Component, ErrorInfo } from "react";
import { useLocation } from "react-router-dom";
import AnotherNewsEventListComponents from "../../components/newseventComponents/AnotherNewsEventListComponents";
import { fetchNewsAnother } from "../../../domain/usecases/fetchNews";
import SpinnerLoading from "../../components/spinnerLoading/SpinnerLoading";
import { NewsEventModel } from "../../../domain/entities/NewsEventModel";
import FormattedDate from "../../components/utilsComponents/FormattedDateComponents";

// Error Boundary Component
class ErrorBoundary extends Component<{ children: React.ReactNode }, { hasError: boolean }> {
    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error) {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("Error caught by boundary: ", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return <div>Something went wrong with the News.</div>;
        }
        return this.props.children;
    }
}

const NewsDetailPage: React.FC = () => {
    // Access the passed state
    const location = useLocation();
    const { newsEvent } = location.state || {}; // This looks correct

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [news, setNews] = useState<NewsEventModel[]>([]);

    // Fetch news
    useEffect(() => {
        const fetchingNewsAnother = async () => {
            try {
                const newsData = await fetchNewsAnother();

                const newsDataItem = newsData.map((item) => item);

                setNews(newsDataItem);
            } catch (err) {
                console.error("Failed to fetch news:", err);
                setError("Failed to load news. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        fetchingNewsAnother();
    }, []);

    // Handle loading state
    if (loading) {
        return <SpinnerLoading />;
    }

    // Handle error state
    if (error) {
        return <div className="text-center text-red-500">{error}</div>;
    }

    return(
        <div className="flex flex-col md:flex-row mt-8">
            <div className="flex flex-1 flex-col ml-3 md:ml-20 mr-3 md:mr-4 my-3 md:my-12">
                {/* Change these to match your actual model properties */}
                <p className="text-base md:text-lg text-gray-800 font-poppins font-semibold text-start mt-6 md:mt-10">
                    {newsEvent?.news_title || "Judul Berita dan Acara"}
                </p>

                <FormattedDate 
                    dateString={newsEvent.news_date_post} 
                    className="text-sm md:text-sm text-gray-800 font-poppins font-normal text-start mt-1 md:mt-2" 
                />
                <img 
                    src={newsEvent?.path || "https://www.nvidia.com/content/nvidiaGDC/id/id_ID/geforce/graphics-cards/_jcr_content/root/responsivegrid/nv_container_1965276325/nv_teaser_copy_1362348342.coreimg.100.850.jpeg/1735896301876/geforce-rtx-40-series-new.jpeg"} 
                    alt="berita" 
                    className="w-full h-auto md:h-auto mt-2 md:mt-3" 
                />
                <p className="text-sm md:text-sm text-gray-800 font-poppins font-normal text-start mt-1 md:mt-2">
                    {newsEvent?.news_image_caption || "Image caption"}
                </p>
                <p 
                    className="text-sm md:text-base text-gray-800 font-poppins font-normal text-justify mt-2 md:mt-3"
                    dangerouslySetInnerHTML={{ __html: newsEvent?.news_content || "Deskripsi berita dan acara" }}
                />
            </div>

            {/* Right Section */}
            <div className="flex flex-col w-96 mt-6 md:mt-20">
                <ErrorBoundary>
                    <AnotherNewsEventListComponents newsList={news} />
                </ErrorBoundary>
            </div>
        </div>
    );
}

export default NewsDetailPage;